import { defineStore } from "pinia";
import AuthService from "@/services/authService";
import { logger } from "@/utils/logger";
const MODULE_KEY_MAP = {
    fleet: "fleet_optimization",
    frequency: "frequency_optimization",
    gtfs: "gtfs",
    competition: "competition_analysis",
    route: "route_optimization",
    task: "task_assignment",
    bi: "business_intelligence",
};
/**
 * Auth & lisans store (Pinia).
 * Vuex kaynak: store/modules/auth.js — davranış birebir.
 * Oturum kalıcılığı AuthService (transportation_auth) üzerinden; Pinia persist yok (çakışma önleme).
 */
function getInitialUser() {
    return AuthService.hasValidSession()
        ? AuthService.getCurrentUser() || null
        : null;
}
export const useAuthStore = defineStore("auth", {
    state: () => ({
        user: getInitialUser(),
        tenantLicense: null,
        licensedModules: [],
        licenseLoaded: false,
        sessionBackendVerified: false,
    }),
    getters: {
        isLoggedIn: (state) => !!state.user && AuthService.hasValidSession(),
        currentUser: (state) => state.user,
        userRole: (state) => state.user?.role || "user",
        isSuperAdmin: (state) => state.user?.role === "superadmin",
        isAdmin: (state) => state.user?.role === "admin" || state.user?.role === "superadmin",
        isUser: (state) => state.user?.role === "user",
        tenantActive: (state) => state.tenantLicense?.active ?? true,
        licensedModuleNames: (state) => state.licensedModules
            .filter((m) => m.aktif_mi)
            .map((m) => m.modul_adi),
        hasModuleAccess() {
            return (moduleKey) => {
                const role = this.userRole;
                if (role === "superadmin" || role === "admin") {
                    const moduleName = MODULE_KEY_MAP[moduleKey] || moduleKey;
                    const mod = this.licensedModules.find((m) => m.modul_adi === moduleName);
                    if (!mod)
                        return true;
                    return mod.aktif_mi;
                }
                const permissions = this.user?.permissions || {};
                const moduleName = MODULE_KEY_MAP[moduleKey] || moduleKey;
                return moduleName in permissions && !!permissions[moduleName];
            };
        },
    },
    actions: {
        clearSession() {
            localStorage.removeItem("transportation_auth");
            this.user = null;
            this.tenantLicense = null;
            this.licensedModules = [];
            this.licenseLoaded = false;
            this.sessionBackendVerified = false;
        },
        async ensureSession() {
            const user = AuthService.getCurrentUser();
            if (!user) {
                if (this.user) {
                    this.clearSession();
                }
                return false;
            }
            if (!AuthService.hasValidSession()) {
                const refreshed = await AuthService.refreshTokenIfNeeded();
                if (!refreshed || !AuthService.hasValidSession()) {
                    this.clearSession();
                    return false;
                }
            }
            if (!this.sessionBackendVerified) {
                let valid = await AuthService.validateToken();
                if (!valid) {
                    const refreshed = await AuthService.refreshSession();
                    valid = refreshed && (await AuthService.validateToken());
                }
                if (!valid) {
                    this.clearSession();
                    return false;
                }
                this.sessionBackendVerified = true;
            }
            this.user = AuthService.getCurrentUser();
            return true;
        },
        async loadUser() {
            const isValid = await this.ensureSession();
            if (isValid) {
                await this.loadLicenseData();
            }
        },
        async loadLicenseData() {
            try {
                const [licenseRes, modulesRes] = await Promise.all([
                    AuthService.getTenantLicense(),
                    AuthService.getTenantModules(),
                ]);
                if (licenseRes) {
                    this.tenantLicense = licenseRes;
                }
                if (modulesRes) {
                    this.licensedModules = modulesRes;
                    this.licenseLoaded = true;
                }
            }
            catch (err) {
                logger.warn("Lisans verisi yüklenemedi:", err);
                this.licensedModules = [];
                this.licenseLoaded = true;
            }
        },
        async login({ username, password }) {
            const result = await AuthService.login(username, password);
            if (result.success) {
                this.user = result.user;
                this.sessionBackendVerified = true;
                await this.loadLicenseData();
            }
            return result;
        },
        async logout() {
            await AuthService.logout();
            this.clearSession();
        },
        updateUserPermissions(permissions) {
            if (this.user) {
                this.user = { ...this.user, permissions };
            }
        },
    },
});
